/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Getting Started Guide - Teen-friendly help page for new users
 * Redesigned with modern, gamified aesthetic
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Brain,
  Calculator,
  Calendar,
  ChevronRight,
  Eye,
  LayoutDashboard,
  PenTool,
  PlayCircle,
  Rocket,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  Users,
  Zap
} from 'lucide-react'
import { useBranding } from '../../stores/brandingStore'
import GuideFigure from '../../components/help/GuideFigure'
import { useAuthStore } from '../../stores/authStore'

const GettingStarted = () => {
  const branding = useBranding()
  const [showGuardianSection, setShowGuardianSection] = useState(false)
  const user = useAuthStore((state) => state.user)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const steps = [
    {
      icon: Target,
      title: 'Pick Your Challenge',
      description: 'Choose SAT Math, Reading & Writing, or quick Mini Tests based on your goals.',
      color: 'from-primary-500 to-primary-600'
    },
    {
      icon: Brain,
      title: 'Answer Questions',
      description: 'Work through adaptive questions that automatically adjust to your skill level.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: TrendingUp,
      title: 'Get Your Report',
      description: 'See your strengths, weaknesses, and exactly what to study next.',
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: Trophy,
      title: 'Level Up',
      description: 'Master topics, climb the rankings, and reach Level 10 SAT readiness!',
      color: 'from-green-500 to-green-600'
    }
  ]

  const challenges = [
    {
      icon: Calculator,
      title: 'SAT Math',
      description: 'Full adaptive practice covering algebra, geometry, and data analysis',
      color: 'bg-gradient-to-br from-primary-600 to-primary-800',
      link: '/challenge-type'
    },
    {
      icon: PenTool,
      title: 'SAT Reading & Writing',
      description: 'Master reading comprehension, grammar, and expression',
      color: 'bg-gradient-to-br from-purple-600 to-purple-800',
      link: '/challenge-type'
    },
    {
      icon: Zap,
      title: 'Mini Tests',
      description: 'Quick 5-10 minute daily practice to build momentum',
      color: 'bg-gradient-to-br from-orange-500 to-orange-700',
      link: '/challenge-type'
    }
  ]

  const weekPlan = [
    {
      day: 'Day 1',
      label: 'Mon',
      focus: 'Launch',
      task: 'Take a Mini Test in Math or Reading & Writing',
      duration: '15 min',
      emoji: '🚀',
      color: 'from-blue-500 to-blue-600'
    },
    {
      day: 'Day 2',
      label: 'Tue',
      focus: 'Discover',
      task: 'Review your report, study weak areas offline',
      duration: '20 min',
      emoji: '🔍',
      color: 'from-purple-500 to-purple-600'
    },
    {
      day: 'Day 3',
      label: 'Wed',
      focus: 'Power Up',
      task: 'Full Challenge in Math',
      duration: '45 min',
      emoji: '⚡',
      color: 'from-orange-500 to-orange-600'
    },
    {
      day: 'Day 4',
      label: 'Thu',
      focus: 'Level Up',
      task: 'Mini Test + review your reports',
      duration: '20 min',
      emoji: '📈',
      color: 'from-green-500 to-green-600'
    },
    {
      day: 'Day 5',
      label: 'Fri',
      focus: 'Conquer',
      task: 'Full Challenge in Reading & Writing',
      duration: '45 min',
      emoji: '🏆',
      color: 'from-primary-500 to-primary-600'
    },
    {
      day: 'Day 6',
      label: 'Sat',
      focus: 'Boost',
      task: '2-3 Mini Tests to build momentum',
      duration: '25 min',
      emoji: '🔥',
      color: 'from-red-500 to-red-600'
    },
    {
      day: 'Day 7',
      label: 'Sun',
      focus: 'Victory',
      task: 'Check rankings, celebrate your wins!',
      duration: '10 min',
      emoji: '🎉',
      color: 'from-yellow-500 to-yellow-600'
    }
  ]

  const stats = [
    { label: 'Growing Community', icon: Users },
    { label: 'Unlimited Questions', icon: Target },
    { label: 'Adaptive Learning', icon: TrendingUp },
    { label: 'Level-Based Progress', icon: Trophy }
  ]

  const screenshots = [
    {
      src: '03-in-challenge.png',
      alt: 'Question interface during a challenge',
      caption: 'Interactive question interface with confidence tracking',
      focusArea: 'center'
    },
    {
      src: '04-report-overview.png',
      alt: 'Challenge report showing your score',
      caption: 'Instant feedback with detailed scoring',
      focusArea: 'top'
    },
    {
      src: '05-report-what-to-study.png',
      alt: 'Study recommendations based on your performance',
      caption: 'Personalized study recommendations',
      focusArea: 'top'
    },
    {
      src: '06-rankings.png',
      alt: 'Rankings leaderboard',
      caption: 'Compete and climb the leaderboard',
      focusArea: 'top'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden px-4 py-16 sm:py-20 sm:px-6 lg:px-8">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary-600/20 via-purple-600/10 to-transparent dark:from-primary-900/40 dark:via-purple-900/20" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary-500/20 dark:bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute top-20 right-0 w-[300px] h-[300px] bg-purple-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-primary-100 dark:bg-primary-900/40 border border-primary-200 dark:border-primary-700 text-primary-700 dark:text-primary-300 text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            <span>Your SAT Journey Starts Here</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-gray-900 via-primary-700 to-purple-700 dark:from-white dark:via-primary-300 dark:to-purple-300 bg-clip-text text-transparent">
            Level Up Your SAT Score
          </h1>

          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-10">
            Join students crushing their SAT goals with adaptive practice, instant feedback, and a
            gamified learning system that actually works.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/challenge-type"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold text-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 hover:scale-105"
            >
              <Rocket className="w-5 h-5" />
              Start Your First Challenge
            </Link>
            {user ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 font-semibold text-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300"
              >
                <LayoutDashboard className="w-5 h-5" />
                Go to Dashboard
              </Link>
            ) : (
              <a
                href="https://www.deaveris.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 font-semibold text-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300"
              >
                <PlayCircle className="w-5 h-5" />
                Learn More
              </a>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="p-5 rounded-2xl bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 backdrop-blur-sm"
              >
                <stat.icon className="w-7 h-7 text-primary-500 dark:text-primary-400 mb-3 mx-auto" />
                <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-4 py-16 sm:py-20 sm:px-6 lg:px-8 bg-white dark:bg-gray-800/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              How It Works
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg max-w-xl mx-auto">
              Four simple steps to transform your SAT prep
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative p-6 rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-300 group"
              >
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-white dark:bg-gray-900 border-2 border-primary-500 flex items-center justify-center text-sm font-bold text-primary-600 dark:text-primary-400">
                  {index + 1}
                </div>
                <div
                  className={`w-14 h-14 rounded-xl bg-gradient-to-r ${step.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  <step.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                  {step.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge Types */}
      <section className="px-4 py-16 sm:py-20 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full bg-purple-100 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300 text-sm font-medium">
              <Target className="w-4 h-4" />
              <span>Choose Your Path</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              Challenge Types
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg max-w-xl mx-auto">
              Multiple ways to practice that fit your schedule and goals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <Link
                key={index}
                to={challenge.link}
                className={`${challenge.color} p-6 rounded-2xl hover:scale-[1.03] transition-all duration-300 group text-white`}
              >
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <challenge.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{challenge.title}</h3>
                <p className="text-white/80 mb-4 text-sm">{challenge.description}</p>
                <div className="flex items-center gap-2 text-white font-medium text-sm group-hover:gap-3 transition-all duration-300">
                  Start Challenge
                  <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Gallery */}
      <section className="px-4 py-16 sm:py-20 sm:px-6 lg:px-8 bg-white dark:bg-gray-800/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full bg-primary-100 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 text-primary-700 dark:text-primary-300 text-sm font-medium">
              <Eye className="w-4 h-4" />
              <span>Preview</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              See It In Action
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg max-w-xl mx-auto">
              Take a peek at what your learning experience looks like
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {screenshots.map((screenshot, index) => (
              <GuideFigure
                key={index}
                src={screenshot.src}
                alt={screenshot.alt}
                caption={screenshot.caption}
                number={index + 1}
                compact={true}
                focusArea={screenshot.focusArea}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 7-Day Plan */}
      <section className="px-4 py-16 sm:py-20 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-purple-50 to-orange-50 dark:from-primary-900/10 dark:via-purple-900/10 dark:to-orange-900/10" />
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-200/30 dark:bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-200/30 dark:bg-purple-500/10 rounded-full blur-3xl" />

        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full bg-gradient-to-r from-primary-100 to-purple-100 dark:from-primary-900/40 dark:to-purple-900/40 border border-primary-200 dark:border-primary-700 text-primary-700 dark:text-primary-300 text-sm font-medium">
              <Calendar className="w-4 h-4" />
              <span>Your Epic First Week</span>
              <Sparkles className="w-4 h-4" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-primary-600 via-purple-600 to-orange-500 dark:from-primary-400 dark:via-purple-400 dark:to-orange-400 bg-clip-text text-transparent">
              7-Day Power-Up Plan
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg max-w-xl mx-auto">
              Your game plan to SAT success. Each day brings you closer to your goals!
            </p>
          </div>

          {/* Desktop: Horizontal timeline */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Connecting line */}
              <div className="absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 via-orange-500 via-green-500 via-primary-500 via-red-500 to-yellow-500 rounded-full" />

              <div className="grid grid-cols-7 gap-4">
                {weekPlan.map((day, index) => (
                  <div key={index} className="flex flex-col items-center group">
                    {/* Day card */}
                    <div
                      className={
                        'relative w-full p-4 rounded-2xl bg-white dark:bg-gray-800 border-2 border-transparent hover:border-primary-400 dark:hover:border-primary-500 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2'
                      }
                    >
                      {/* Emoji badge */}
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-2xl">
                        {day.emoji}
                      </div>

                      <div className="text-center pt-2">
                        <div className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                          {day.label}
                        </div>
                        <div
                          className={`mt-2 w-12 h-12 rounded-xl bg-gradient-to-r ${day.color} mx-auto flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                        >
                          <span className="text-lg font-bold text-white">{index + 1}</span>
                        </div>
                        <div className="mt-3 text-sm font-bold text-gray-900 dark:text-white">
                          {day.focus}
                        </div>
                        <div className="mt-1 text-xs text-primary-600 dark:text-primary-400 font-medium">
                          {day.duration}
                        </div>
                      </div>
                    </div>

                    {/* Task description below */}
                    <div className="mt-4 text-center px-1">
                      <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                        {day.task}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile/Tablet: Stacked cards */}
          <div className="lg:hidden space-y-4">
            {weekPlan.map((day, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border-2 border-transparent hover:border-primary-400 dark:hover:border-primary-500 transition-all duration-300"
              >
                {/* Day number with gradient */}
                <div
                  className={`flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-r ${day.color} flex items-center justify-center shadow-lg`}
                >
                  <span className="text-xl font-bold text-white">{day.emoji}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase">
                      {day.label}
                    </span>
                    <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                      {day.duration}
                    </span>
                  </div>
                  <div className="text-base font-bold text-gray-900 dark:text-white mt-0.5">
                    {day.focus}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{day.task}</p>
                </div>

                {/* Day badge */}
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-500 dark:text-gray-400">
                    {index + 1}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Repeat cycle callout */}
          <div className="mt-10 p-6 bg-gradient-to-r from-primary-500 via-purple-500 to-orange-500 rounded-2xl shadow-xl text-white text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Sparkles className="w-6 h-6" />
              <span className="text-xl font-bold">Then Level Up!</span>
              <Sparkles className="w-6 h-6" />
            </div>
            <p className="text-white/90 max-w-lg mx-auto">
              Repeat the cycle each week. Check your reports to see what's improving and adjust your
              focus. Watch your level climb higher and higher!
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 py-16 sm:py-20 sm:px-6 lg:px-8 bg-white dark:bg-gray-800/50">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900 dark:text-white">
                Everything You Need to{' '}
                <span className="bg-gradient-to-r from-primary-500 to-purple-500 bg-clip-text text-transparent">
                  Succeed
                </span>
              </h2>
              <div className="space-y-5">
                {[
                  {
                    icon: Brain,
                    title: 'AI-Powered Adaptive Learning',
                    description:
                      'Questions automatically adjust to your skill level for optimal challenge'
                  },
                  {
                    icon: Target,
                    title: 'Unlimited Practice Questions',
                    description:
                      'Ever-growing library covering all SAT topics with detailed explanations'
                  },
                  {
                    icon: TrendingUp,
                    title: 'Detailed Progress Analytics',
                    description: 'Track your improvement with insights on strengths and weaknesses'
                  },
                  {
                    icon: Trophy,
                    title: 'Gamified Level System',
                    description: 'Earn levels, track progress, and compete on leaderboards'
                  }
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="flex gap-4 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-300"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-100 to-purple-100 dark:from-primary-900/40 dark:to-purple-900/40 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1 text-gray-900 dark:text-white">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-purple-500/20 rounded-3xl blur-3xl" />
              <div className="relative p-8 rounded-3xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-r from-primary-500 to-purple-500 mx-auto mb-4 flex items-center justify-center">
                    <span className="text-3xl font-bold text-white">10</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Your Level Goal
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mt-2">Level 10 = SAT Ready</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600 dark:text-gray-400">Math Progress</span>
                      <span className="text-primary-600 dark:text-primary-400 font-medium">
                        Level Up!
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full w-3/4 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full" />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600 dark:text-gray-400">Reading & Writing</span>
                      <span className="text-purple-600 dark:text-purple-400 font-medium">
                        Keep Going!
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full w-2/3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full" />
                    </div>
                  </div>
                  <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">Overall Progress</span>
                      <span className="text-xl font-bold text-green-500">On Track!</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guardian Section - Collapsible */}
      <section className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-2xl p-6 border border-primary-200 dark:border-primary-800">
            <button
              onClick={() => setShowGuardianSection(!showGuardianSection)}
              className="w-full flex items-center justify-between gap-4 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary-100 dark:bg-primary-900/40 rounded-xl">
                  <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-primary-900 dark:text-primary-100">
                    For Parents & Guardians
                  </h2>
                  <p className="text-sm text-primary-700 dark:text-primary-300 mt-0.5">
                    Click to learn how you can support your student
                  </p>
                </div>
              </div>
              <ChevronRight
                className={`w-6 h-6 text-primary-600 dark:text-primary-400 transition-transform ${
                  showGuardianSection ? 'rotate-90' : ''
                }`}
              />
            </button>

            {showGuardianSection && (
              <div className="mt-6 pt-6 border-t border-primary-200 dark:border-primary-700 space-y-4">
                <p className="text-primary-800 dark:text-primary-200">
                  As a guardian, you can monitor your student's progress without seeing their
                  specific answers. You'll receive email updates when they complete challenges and
                  level up, helping you stay engaged with their SAT preparation journey.
                </p>
                <p className="text-sm text-primary-700 dark:text-primary-300">
                  Students can invite guardians from their profile settings, or guardians can
                  request to be added to a student's account.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-16 sm:py-20 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary-600 via-primary-700 to-purple-700 p-8 sm:p-12 text-white">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/20 rounded-full blur-2xl" />

            <div className="relative text-center">
              <Rocket className="w-14 h-14 mx-auto mb-4" />
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Start Your Journey?</h2>
              <p className="text-primary-100 text-lg mb-8 max-w-xl mx-auto">
                Join students who are leveling up their SAT scores with our proven adaptive learning
                system. Your first challenge is waiting!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/challenge-type"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white text-primary-700 font-semibold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105"
                >
                  <PlayCircle className="w-5 h-5" />
                  Start Your First Challenge
                  <ChevronRight className="w-5 h-5" />
                </Link>
                {user ? (
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white/20 border border-white/30 text-white font-semibold text-lg hover:bg-white/30 transition-all duration-300"
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    Go to Dashboard
                  </Link>
                ) : (
                  <a
                    href="https://www.deaveris.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white/20 border border-white/30 text-white font-semibold text-lg hover:bg-white/30 transition-all duration-300"
                  >
                    Join the Waitlist
                    <ChevronRight className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <section className="px-4 py-8 sm:px-6 lg:px-8 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-5xl mx-auto text-center text-gray-500 dark:text-gray-400 text-sm">
          <p>{branding.copyrightText}. All rights reserved.</p>
        </div>
      </section>
    </div>
  )
}

export default GettingStarted
